import { useEffect } from "react"
import { Localized, useLocalization } from "@fluent/react"
import { Box, IconButton, Stack, Tooltip, Typography } from "@mui/material"
import FormatBoldIcon from "@mui/icons-material/FormatBold"
import FormatItalicIcon from "@mui/icons-material/FormatItalic"
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted"
import FormatQuoteIcon from "@mui/icons-material/FormatQuote"
import { EditorContent, useEditor } from "@tiptap/react"
import { tiptapExtensions } from "../../app/RichTextEditor"

export const NoteRichTextEditor = ({ value, onValueChange }: {
    value: string
    onValueChange: (html: string) => void
}) => {
    const { l10n } = useLocalization()
    const editor = useEditor({
        extensions: tiptapExtensions,
        content: value,
        onUpdate: ({ editor }) => onValueChange(editor.getHTML()),
        editorProps: {
            attributes: {
                role: 'textbox',
                'aria-label': l10n.getString("notes-content", null, "_Note content"),
                'aria-multiline': 'true',
            },
        },
    })

    useEffect(() => {
        if (editor && editor.getHTML() !== value) editor.commands.setContent(value, false)
    }, [editor, value])

    if (!editor) return null

    const tools = [
        { id: 'rich-text-bold', fallback: '_Bold', Icon: FormatBoldIcon, active: editor.isActive('bold'), action: () => editor.chain().focus().toggleBold().run() },
        { id: 'rich-text-italic', fallback: '_Italic', Icon: FormatItalicIcon, active: editor.isActive('italic'), action: () => editor.chain().focus().toggleItalic().run() },
        { id: 'rich-text-bullet-list', fallback: '_Bullet list', Icon: FormatListBulletedIcon, active: editor.isActive('bulletList'), action: () => editor.chain().focus().toggleBulletList().run() },
        { id: 'rich-text-quote', fallback: '_Quote', Icon: FormatQuoteIcon, active: editor.isActive('blockquote'), action: () => editor.chain().focus().toggleBlockquote().run() },
    ]

    return <Box>
        <Typography variant="caption" color="text.secondary">
            <Localized id="notes-content">_Note content</Localized>
        </Typography>
        <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, bgcolor: 'background.paper' }}>
            <Stack direction="row" spacing={0.25} sx={{ px: 0.5, py: 0.25, borderBottom: 1, borderColor: 'divider' }}>
                {tools.map(({ id, fallback, Icon, active, action }) => {
                    const label = l10n.getString(id, null, fallback)
                    return <Tooltip key={id} title={label}>
                        <IconButton size="small" onClick={action} aria-label={label} aria-pressed={active}
                            color={active ? 'primary' : 'default'}>
                            <Icon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                })}
            </Stack>
            <Box sx={{
                px: 1, py: 0.75, maxHeight: 200, overflowY: 'auto',
                '& .ProseMirror': { minHeight: 76, outline: 'none', overflowWrap: 'anywhere' },
                '& .ProseMirror p': { my: 0.25 },
                '& .ProseMirror ul, & .ProseMirror ol': { my: 0.5, pl: 2.5 },
            }}>
                <EditorContent editor={editor} />
            </Box>
        </Box>
    </Box>
}
